import { endpoints } from '@gopinhos/shared';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Bell, Clock, FolderKanban } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { apiClient } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';
import { dateLocaleFor } from '../lib/i18n';

interface RemindersResp {
  items: Array<{
    id: string;
    dueAt: string;
    message: string | null;
    lead: { id: string; title: string; projectId: string };
  }>;
}

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const reminders = useQuery({
    queryKey: ['reminders', 'me'],
    queryFn: () =>
      apiClient.request<unknown, never>({
        method: 'GET',
        path: endpoints.myReminders(),
      }) as Promise<RemindersResp>,
  });
  const notifications = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () =>
      apiClient.request<unknown, never>({
        method: 'GET',
        path: endpoints.notifications() + '?unreadOnly=true',
      }),
  });

  const firstName = user?.fullName ? user.fullName.split(' ')[0] : '';
  const greeting = t('dashboard.greeting', { nameSuffix: firstName ? `, ${firstName}` : '' });
  const locale = dateLocaleFor(i18n.resolvedLanguage ?? i18n.language);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{greeting}</h1>
        <p className="text-sm text-surface-500">{t('dashboard.subtitle')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-brand-600" /> {t('dashboard.today')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reminders.isLoading ? (
              <Skeleton className="h-20" />
            ) : reminders.data?.items.length ? (
              <ul className="space-y-2 text-sm">
                {reminders.data.items.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex items-center justify-between">
                    <span className="truncate">{r.lead.title}</span>
                    <span className="text-xs text-surface-500">
                      {formatDistanceToNow(new Date(r.dueAt), { addSuffix: true, locale })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-surface-500">{t('dashboard.noReminders')}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <FolderKanban className="h-4 w-4 text-brand-600" /> {t('dashboard.myLeadsMoving')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-surface-500">{t('dashboard.slaPlaceholder')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Bell className="h-4 w-4 text-brand-600" /> {t('dashboard.recentActivity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.isLoading ? (
              <Skeleton className="h-20" />
            ) : (
              <p className="text-sm text-surface-500">
                {t('dashboard.unreadCount', {
                  count: ((notifications.data as { items?: unknown[] } | undefined)?.items ?? []).length,
                })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
